import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import VideoSessionWhereby from '../components/VideoSessionWhereby';
import { firebaseAppointmentService, Appointment } from '../services/firebase/FirebaseAppointmentService';
import FirebaseActivityService from '../services/firebase/FirebaseActivityService';
import NotificationTriggerService from '../services/NotificationTriggerService';
import { PostSessionResult } from '../services/PostSessionPipelineService';
import PostSessionReviewModal from '../components/PostSessionReviewModal';

/**
 * Standalone video session page — opens in a popup window.
 * Keeps the main dashboard accessible while in a call.
 */
const VideoSessionPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user, userProfile } = useAuth();
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [postSessionResult, setPostSessionResult] = useState<{ result: PostSessionResult; appointment: Appointment } | null>(null);

  const appointmentId = searchParams.get('appointmentId');
  const role = (searchParams.get('role') || 'client') as 'provider' | 'client';
  const resolvedPatientId = userProfile?.patientId || user?.uid || '';

  // Load appointment data from Firestore
  useEffect(() => {
    if (!appointmentId) {
      setError('No appointment ID provided');
      setLoading(false);
      return;
    }

    const loadAppointment = async () => {
      try {
        const appt = await firebaseAppointmentService.getAppointment(appointmentId);
        if (!appt) {
          setError('Appointment not found');
        } else {
          setAppointment(appt);
        }
      } catch (err) {
        console.error('Failed to load appointment:', err);
        setError('Failed to load appointment');
      }
      setLoading(false);
    };

    loadAppointment();
  }, [appointmentId]);

  // Notify parent window on unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (window.opener && !sessionEnded) {
        window.opener.postMessage({ type: 'video-session-closed', appointmentId }, '*');
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [appointmentId, sessionEnded]);

  const handleEndSession = async (pipelineResult?: PostSessionResult) => {
    if (!appointment) return;

    setSessionEnded(true);

    // Mark appointment as completed
    try {
      await firebaseAppointmentService.completeAppointment(appointment.id);
    } catch (err) {
      console.error('Failed to mark appointment as completed:', err);
    }

    // Log activity (provider only)
    if (role === 'provider' && user?.uid) {
      try {
        await FirebaseActivityService.getInstance().logActivity({
          providerId: user.uid,
          clientId: appointment.patientId,
          clientName: appointment.patientName || 'Client',
          activityType: 'appointment_completed',
          title: 'Video Session Completed',
          description: `Video therapy session with ${appointment.patientName || 'client'} completed.`,
          metadata: { appointmentId: appointment.id }
        });
      } catch (err) {
        console.error('Failed to log session activity:', err);
      }

      // Send post-session notification
      if (appointment.patientId) {
        const aptDate = new Date(appointment.appointmentDate);
        const dateStr = new Date(aptDate.getUTCFullYear(), aptDate.getUTCMonth(), aptDate.getUTCDate())
          .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        NotificationTriggerService.getInstance().sendPostSession({
          patientId: appointment.patientId,
          providerId: user.uid,
          appointmentDate: dateStr,
          appointmentId: appointment.id,
        }).catch(() => {});
      }
    }

    // Notify parent dashboard that session ended
    if (window.opener) {
      window.opener.postMessage({
        type: 'video-session-ended',
        appointmentId: appointment.id,
        pipelineResult: pipelineResult ? true : false
      }, '*');
    }

    // Show post-session review (provider only) or close window (client)
    if (pipelineResult && role === 'provider') {
      setPostSessionResult({ result: pipelineResult, appointment });
    } else {
      // Close popup after a brief delay
      setTimeout(() => window.close(), 1500);
    }
  };

  // Set window title
  useEffect(() => {
    if (appointment) {
      document.title = `Video Session - ${appointment.patientName || 'Patient'}`;
    } else {
      document.title = 'Video Session - ClinicCore';
    }
  }, [appointment]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#0f172a',
        color: '#f9fafb',
        fontSize: '18px'
      }}>
        Loading session...
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#0f172a',
        color: '#f9fafb',
        gap: '16px'
      }}>
        <h2 style={{ color: '#ef4444' }}>{error || 'Session not available'}</h2>
        <button
          onClick={() => window.close()}
          style={{
            padding: '12px 24px',
            backgroundColor: '#374151',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Close Window
        </button>
      </div>
    );
  }

  if (sessionEnded && !postSessionResult) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#0f172a',
        color: '#f9fafb',
        gap: '16px'
      }}>
        <div style={{ fontSize: '48px' }}>✓</div>
        <h2 style={{ color: '#10b981' }}>Session Complete</h2>
        <p style={{ color: '#d1d5db' }}>This window will close automatically.</p>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', backgroundColor: '#0f172a' }}>
      {postSessionResult ? (
        <PostSessionReviewModal
          result={postSessionResult.result}
          appointmentId={postSessionResult.appointment.id}
          patientId={postSessionResult.appointment.patientId}
          patientName={postSessionResult.appointment.patientName || 'Patient'}
          providerId={user?.uid || ''}
          onClose={() => {
            setPostSessionResult(null);
            window.close();
          }}
          onNoteSaved={() => console.log('[PostSession] Clinical note saved from popup')}
        />
      ) : (
        <VideoSessionWhereby
          appointmentId={appointment.id}
          patientId={appointment.patientId}
          patientName={appointment.patientName || 'Patient'}
          patient2Id={(appointment as any).patient2Id}
          patient2Name={(appointment as any).patient2Name}
          providerId={role === 'provider' ? (user?.uid || '') : (appointment as any).providerId}
          providerName={role === 'provider' ? (userProfile?.displayName || 'Provider') : ((appointment as any).providerName || 'Provider')}
          role={role}
          currentUserId={role === 'client' ? resolvedPatientId : user?.uid}
          onEndSession={handleEndSession}
        />
      )}
    </div>
  );
};

export default VideoSessionPage;
